import { toRange, translate } from '@studio/js-utils/range';
import type { Ambit, PTZ } from '@studio/models/ptz';
import { hasMovement, Range as PtzRange } from '@studio/models/ptz';
import { toObjectEntries } from '@tryiris-ai/js-types';
import type { T_Optional } from '@tryiris-ai/js-types';
import { Range } from './constants.js';
import type { Transformer } from './types.js';
import { clamp } from './utils.js';

/** Throttles movement speed. */
export class Throttle implements Transformer {
  /** Throttle ambit. */
  #ambit: Ambit;

  /** Indicates if throttling is enabled. */
  #isEnabled: boolean;

  /** Speed limits. */
  #limit: number;

  /**
   * Throttles movement speed for the specified axis ambit.
   *
   * @param ambit - Axis ambit.
   * @param args - Throttle arguments.
   * @param args.isEnabled - `true` if the throttle transformer is enabled, `false` otherwise
   *   (default `true`).
   * @param args.limit - Default speed limit (default `Range.max`).
   */
  public constructor(ambit: Ambit, args?: T_Optional<{ isEnabled: boolean; limit: number }>) {
    this.#ambit = ambit;
    this.#isEnabled = args?.isEnabled ?? true;
    this.#limit = args?.limit ?? Range.max;
  }

  /**
   * Determines if the throttle transformer is enabled.
   *
   * @returns `true` if the throttle transformer is enabled, `false` otherwise.
   */
  public get isEnabled(): boolean {
    return this.#isEnabled;
  }

  /**
   * Enables or disables the throttle transformer.
   *
   * @param isEnabled - `true` to enable the throttle transformer, `false` to disable it.
   */
  public set isEnabled(isEnabled: boolean) {
    this.#isEnabled = isEnabled;
  }

  /**
   * Set a rate limit.
   *
   * @param value - The speed limit.
   * @returns The current instance.
   */
  public setLimit(value: number): this {
    this.#limit = clamp(value);

    return this;
  }

  /**
   * Applies throttling to the specified movement intent.
   *
   * @param intent - The PTZ intent to be transformed.
   * @returns The transformed PTZ intent.
   */
  public transform(intent: PTZ): PTZ {
    if (!this.#isEnabled || !hasMovement(intent, this.#ambit)) {
      return intent;
    }

    const from = PtzRange;
    const to = toRange(-this.#limit, this.#limit);

    return <PTZ>(
      Object.fromEntries(
        toObjectEntries(intent).map(([axis, magnitude]) => [
          axis,
          this.#ambit[axis] ? translate(magnitude, from, to) : magnitude
        ])
      )
    );
  }
}
